/**
 * @file authService.js
 * @description Servicio de autenticación y autorización
 * @module services/authService
 * 
 * Funcionalidades principales:
 * - Autenticación de usuarios (login/logout)
 * - Gestión de tokens JWT (access/refresh)
 * - Control de permisos por rol y marca
 * - Filtrado de vehículos según permisos
 * - Interceptores HTTP para manejo automático de tokens
 * - Sistema de caché para optimización
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/config';

/**
 * SISTEMA DE CACHÉ
 * Optimiza llamadas repetidas guardando resultados temporalmente
 */
let userInfoCache = null;
let marcasPermitidasCache = null;
let lastCacheTime = 0;
// Caché de marcas activas reales, obtenidas del backend
let marcasDisponiblesCache = null;
const CACHE_DURATION = 5000; // 5 segundos

let vehiculosFilterCache = new Map();
const FILTER_CACHE_DURATION = 30000; // 30 segundos

/**
 * FLAGS DE CONTROL
 * Previenen operaciones concurrentes que podrían causar problemas
 */
let isRedirecting = false;
let isAlreadyClearing = false;

/**
 * INSTANCIA AXIOS CONFIGURADA
 * Todas las peticiones HTTP usan esta instancia con interceptores
 */
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * Limpia el caché de información de usuario y marcas
 * Se ejecuta al login, logout o cuando expira el caché
 */
const clearCache = () => {
  userInfoCache = null;
  marcasPermitidasCache = null;
  lastCacheTime = 0;
};

/**
 * Limpia el caché de vehículos filtrados
 * Se ejecuta cuando cambia el usuario o los permisos
 */
const clearVehicleCache = () => {
  vehiculosFilterCache.clear();
  vehiculosFilterCache._adminLogged = false;
};

/**
 * Limpia TODOS los datos de autenticación del cliente
 * 
 * Elimina:
 * - Tokens de localStorage
 * - Caché de usuario y vehículos
 * - SessionStorage
 * - Cookies
 * - Headers de autorización
 * 
 * @function clearAllAuthData
 * @returns {void}
 */
export const clearAllAuthData = () => {
  if (isAlreadyClearing) return;
  isAlreadyClearing = true;

  try {
    clearCache();
    clearVehicleCache();

    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
    }

    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.clear();
    }

    if (typeof document !== 'undefined' && document.cookie) {
      document.cookie.split(';').forEach(c => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
      });
    }

    delete axiosInstance.defaults.headers.common['Authorization'];

  } catch (err) {
    console.error('Error durante limpieza de sesión:', err);
  } finally {
    isAlreadyClearing = false;
  }
};

/**
 * Fuerza el cierre de sesión sin redirección
 * Útil para el interceptor HTTP que no debe causar redirects
 * 
 * @function forceLogout
 * @returns {void}
 */
export const forceLogout = () => {
  clearAllAuthData();
  clearVehicleCache();
};

/**
 * Establece o remueve el token de autorización en los headers HTTP
 * 
 * @function setAuthToken
 * @param {string|null} token - Token JWT a establecer, null para remover
 * @returns {void}
 */
export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

/**
 * Verifica si el usuario está autenticado validando el token
 * Decodifica el JWT y verifica su fecha de expiración
 * 
 * @function isAuthenticated
 * @returns {boolean} true si el token es válido y no ha expirado
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() < exp * 1000;
  } catch {
    return false;
  }
};

/**
 * Obtiene la información del usuario autenticado con sistema de caché
 * 
 * Estructura del userInfo:
 * {
 *   username: string,
 *   email: string,
 *   rol: number (1: Admin, 2: Director, 3-6: otros roles),
 *   permisos_marcas: { marca: boolean }
 * }
 * 
 * @function getUserInfo
 * @returns {Object|null} Información del usuario o null si no está autenticado
 */
  export const getUserInfo = () => {
    try {
      if (!isAuthenticated()) {
        return null;
      }
      
      const now = Date.now();
      if (userInfoCache && (now - lastCacheTime) < CACHE_DURATION) {
        return userInfoCache;
      }
      
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) return null;
      
      const parsedInfo = JSON.parse(userInfo);
      
      // Validar estructura según tipo de usuario
      if (!parsedInfo.username) {
        clearAllAuthData();
        return null;
      }

      // Vendedor: tiene tipo_usuario, no rol numérico
      if (parsedInfo.tipo_usuario === 'vendedor') {
        userInfoCache = parsedInfo;
        lastCacheTime = now;
        return parsedInfo;
      }

      // Usuario normal: debe tener rol numérico
      if (parsedInfo.rol === undefined) {
        clearAllAuthData();
        return null;
      }
      
      userInfoCache = parsedInfo;
      lastCacheTime = now;
      return parsedInfo;
      
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      clearAllAuthData();
      return null;
    }
  };

/**
 * Verifica si el usuario tiene permiso para ver una marca específica
 * 
 * Roles con acceso total:
 * - 1: Admin
 * - 2: Director General
 * 
 * Otros roles necesitan permisos explícitos en permisos_marcas
 * 
 * @function canViewMarca
 * @param {string} marca - Nombre de la marca (ej: 'FORD', 'MAZDA')
 * @returns {boolean} true si el usuario puede ver la marca
 */
export const canViewMarca = (marca) => {
  const userInfo = getUserInfo();
  
  if (!userInfo) {
    return false;
  }
  
  // Admin tienen acceso total
    // Solo Admin tiene acceso total (temporal, hasta rediseñar el sistema de roles) checar
  if (userInfo.rol === 1) {
    return true;
  }
  
  // Verificar permisos específicos
  const permisosMarcas = userInfo.permisos_marcas || {};
  return permisosMarcas.hasOwnProperty(marca);
};

/**
 * Carga dinámicamente las marcas activas desde el backend
 * y las guarda en caché para getMarcasPermitidas()
 *
 * @async
 * @function cargarMarcasDisponibles
 * @returns {Promise<string[]>} Array de nombres de marcas activas
 */
export const cargarMarcasDisponibles = async () => {
  try {
    const response = await axiosInstance.get('/api/marcas/for-permissions/');
    const marcas = response.data?.marcas || [];
    marcasDisponiblesCache = marcas.map(m => m.nombre.toUpperCase());
    return marcasDisponiblesCache;
  } catch (error) {
    console.error('Error al cargar marcas disponibles:', error);
    return marcasDisponiblesCache || [];
  }
};

/**
 * Obtiene el caché actual de marcas activas del catálogo (síncrono)
 * @function getMarcasDisponibles
 * @returns {string[]} Array de nombres de marcas activas en el catálogo
 */
export const getMarcasDisponibles = () => marcasDisponiblesCache || [];

/**
 * Obtiene el listado de marcas que el usuario puede visualizar
 * Con sistema de caché para optimización
 * 
 * @function getMarcasPermitidas
 * @returns {string[]} Array de nombres de marcas permitidas
 */
export const getMarcasPermitidas = () => {
  const userInfo = getUserInfo();
  
  if (!userInfo) {
    return [];
  }
  
  // Verificar cache
  const now = Date.now();
  if (marcasPermitidasCache && (now - lastCacheTime) < CACHE_DURATION) {
    return marcasPermitidasCache;
  }
  
  // Solo Admin ve todas las marcas (temporal, hasta rediseñar el sistema de roles)
  if (userInfo.rol === 1) {
    marcasPermitidasCache = marcasDisponiblesCache || [];
    return marcasPermitidasCache;
  }
  
  // Otros roles: solo sus marcas asignadas
  const permisosMarcas = userInfo.permisos_marcas || {};
  marcasPermitidasCache = Object.keys(permisosMarcas);
  
  return marcasPermitidasCache;
};

/**
 * Filtra un array de vehículos según los permisos del usuario
 * Implementa sistema de caché para evitar filtrados repetidos
 * 
 * Criterios de filtrado:
 * - Admin/Director: ven todos los vehículos
 * - Otros roles: solo vehículos de sus marcas permitidas
 * 
 * @function filterVehiculosByPermissions
 * @param {Array} vehiculos - Array de objetos de vehículos
 * @returns {Array} Array filtrado de vehículos
 */
export const filterVehiculosByPermissions = (vehiculos) => {
  try {
    if (!Array.isArray(vehiculos) || vehiculos.length === 0) {
      return [];
    }
    
    const userInfo = getUserInfo();
    
    if (!userInfo) {
      return [];
    }
    
    // Generar clave de cache
    const cacheKey = `${userInfo.username}-${vehiculos.length}`;
    const now = Date.now();
    
    // Verificar cache
    if (vehiculosFilterCache.has(cacheKey)) {
      const cached = vehiculosFilterCache.get(cacheKey);
      if ((now - cached.timestamp) < FILTER_CACHE_DURATION) {
        return cached.result;
      }
    }
    
     // Solo Admin ve todo (temporal, hasta rediseñar el sistema de roles)
    if (userInfo.rol === 1) {
      vehiculosFilterCache.set(cacheKey, {
        result: vehiculos,
        timestamp: now
      });
      
      return vehiculos;
    }
    
    // Filtrar por marcas permitidas
    const marcasPermitidas = getMarcasPermitidas();
    const marcasSet = new Set(marcasPermitidas);
    
    const vehiculosFiltrados = vehiculos.filter(vehiculo => {
      if (!vehiculo || !vehiculo.Marca) {
        return false;
      }
      
      return marcasSet.has(vehiculo.Marca);
    });
    
    // Guardar en cache
    vehiculosFilterCache.set(cacheKey, {
      result: vehiculosFiltrados,
      timestamp: now
    });
    
    return vehiculosFiltrados;
    
  } catch (error) {
    console.error('Error filtrando vehículos por permisos:', error);
    return [];
  }
};

/**
 * Inicia sesión de usuario y almacena tokens
 * 
 * Proceso:
 * 1. Envía credenciales al servidor
 * 2. Recibe tokens JWT (access + refresh)
 * 3. Guarda tokens y userInfo en localStorage
 * 4. Configura headers de autorización
 * 5. Limpia cachés anteriores
 * 
 * @async
 * @function login
 * @param {string} username - Nombre de usuario
 * @param {string} password - Contraseña
 * @returns {Promise<Object>} Resultado del login
 * @property {boolean} success - true si el login fue exitoso
 * @property {Object} [user] - Información del usuario (si success=true)
 * @property {string} [message] - Mensaje de error (si success=false)
 */
export const login = async (username, password) => {
  console.log('authService.login called with:', { username, password });
  try {
    console.log('Enviando a:', API_BASE_URL + '/api/auth/login/');
    console.log('axiosInstance baseURL:', axiosInstance.defaults.baseURL);
    console.log('axiosInstance headers:', axiosInstance.defaults.headers);
    
    const response = await axiosInstance.post('/api/auth/login/', 
      JSON.stringify({ username, password }),
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.data) {
      throw new Error('Respuesta de login inválida');
    }

    // Manejar diferentes estructuras de respuesta
    let access, refresh, user;

    if (response.data.tokens) {
      // Estructura antigua: { tokens: { access, refresh } }
      access = response.data.tokens.access;
      refresh = response.data.tokens.refresh;
      user = response.data.user || { username };
    } else {
      // Estructura nueva: { access, refresh, user }
      access = response.data.access;
      refresh = response.data.refresh;
      user = response.data.user;
    }

    if (!access || !refresh) {
      throw new Error('Tokens no recibidos del servidor');
    }

    // Limpiar cache antes de guardar nuevos datos
    clearCache();
    clearVehicleCache();

    // Guardar tokens y userInfo
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('userInfo', JSON.stringify(user));

    // Configurar headers
    setAuthToken(access);

    // Precargar marcas disponibles (dinámico, evita el hardcode)
    await cargarMarcasDisponibles();

    return { success: true, user };

  } catch (error) {
    console.error('Login failed:', error.response?.data || error.message);
    clearAllAuthData();
    return {
      success: false,
      message: error.response?.data?.message ||
               error.response?.data?.error ||
               error.response?.data?.detail ||
               'Error al iniciar sesión. Verifica tus credenciales.',
    };
  }
};
export const validatePassword = async (password) => {
  try {
    const userInfo = getUserInfo();
    
    if (!userInfo || !userInfo.username) {
      return {
        success: false,
        message: 'No hay sesión activa'
      };
    }
    
    if (!password || password.trim() === '') {
      return {
        success: false,
        message: 'La contraseña no puede estar vacía'
      };
    }
    
    // Enviar credenciales al endpoint de validación
    const response = await axiosInstance.post('/api/auth/validate-password/', {
      username: userInfo.username,
      password: password
    });
    
    // Si el servidor responde con éxito, la contraseña es válida
    if (response.data && response.data.valid === true) {
      return {
        success: true
      };
    }
    
    return {
      success: false,
      message: 'Contraseña incorrecta'
    };
    
  } catch (error) {
    console.error('Error validando contraseña:', error);
    
    // Manejar respuestas específicas del servidor
    if (error.response?.status === 400 || error.response?.status === 401) {
      return {
        success: false,
        message: error.response.data?.message || error.response.data?.detail || 'Contraseña incorrecta'
      };
    }
    
    if (error.response?.status === 403) {
      return {
        success: false,
        message: 'No tiene permisos para realizar esta acción'
      };
    }
    
    // Error de red u otro problema
    return {
      success: false,
      message: 'Error al validar la contraseña. Intente nuevamente.'
    };
  }
};
/**
 * Cierra la sesión del usuario
 * Limpia todos los datos y redirige al login
 * 
 * @function logout
 * @returns {void}
 */
export const logout = () => {
  clearAllAuthData();
  clearVehicleCache();
  window.location.href = '/login';
};

/**
 * INTERCEPTOR DE REQUEST
 * Agrega automáticamente el token de autorización a todas las peticiones
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Error en interceptor de request:', error);
    return Promise.reject(error);
  }
);

/**
 * INTERCEPTOR DE RESPONSE
 * Maneja errores 401 (no autorizado) automáticamente
 * Intenta refrescar el token antes de fallar la petición
 * 
 * Flujo de refresh:
 * 1. Detecta error 401
 * 2. Intenta refrescar el access token usando refresh token
 * 3. Si funciona: reintenta la petición original con nuevo token
 * 4. Si falla: fuerza logout
 */
axiosInstance.interceptors.response.use(
  (response) => {
    if (isRedirecting) {
      isRedirecting = false;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Manejar error 401 - Token expirado
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        clearAllAuthData();
        window.dispatchEvent(new CustomEvent('authError', { detail: 'No refresh token' }));
        return Promise.reject(error);
      }
      
      try {
        // Intentar refrescar token
        const refreshResponse = await axios.post(`${API_BASE_URL}/api/auth/token/refresh/`, {
          refresh: refreshToken,
        });
        
        const newAccessToken = refreshResponse.data.access;
        
        if (!newAccessToken) {
          throw new Error('No se recibió nuevo token de acceso');
        }
        
        // Actualizar token y limpiar cache
        localStorage.setItem('accessToken', newAccessToken);
        setAuthToken(newAccessToken);
        clearCache();
        clearVehicleCache();
        
        // Reintentar petición original con nuevo token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        
        return axiosInstance(originalRequest);
        
      } catch (refreshError) {
        console.error('Error al refrescar token:', refreshError.response?.data || refreshError.message);
        clearAllAuthData();
        window.dispatchEvent(new CustomEvent('authError', { detail: 'Refresh failed' }));
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * INICIALIZACIÓN
 * Configura el token al cargar el módulo si existe una sesión válida
 */
const initializeAuth = () => {
  const token = localStorage.getItem('accessToken');
  if (token && isAuthenticated()) {
    setAuthToken(token);
  } else if (token) {
    // Token inválido - limpiar
    clearAllAuthData();
  }
};

initializeAuth();
/**
 * Verifica si el usuario autenticado es un vendedor
 * @returns {boolean}
 */
export const isVendedor = () => {
  const userInfo = getUserInfo();
  return userInfo?.tipo_usuario === 'vendedor';
};
export default axiosInstance;